import React from "react";
import { Autocomplete, Box, TextField, createFilterOptions } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { newSalespersonContact } from "../../../../../api";
import type { Job, Contact } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { CREATE_NEW_SALESPERSON } from "../../../../../utilities/constants";
import { ColumnContext } from "../standard-column-types";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { useSalespersonColumnViewModel } from "./Salesperson.column.vm";
import { SalespersonColumnViewModel } from "./Salesperson.column.types";
import { useValidationErrors } from "../../ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../EditModeContext";

const filter = createFilterOptions<Contact>();

interface SalespersonColumnCellProps {
	row: MRT_Row<Job>;
	vm: SalespersonColumnViewModel;
}

/**
 * Memoized Cell component for Salesperson column
 * Prevents unnecessary re-renders when column definitions change
 */
const SalespersonColumnCell = React.memo(
	function SalespersonColumnCell({ row, vm }: SalespersonColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const isInEditMode = useIsRowInEditMode(job.id);
		const { exitRowEdit } = useEditModeContext();
		const allSalespersons = vm.getAllSalespersons();
		const selectedSalesperson = vm.getSelectedSalesperson(job);
		const isRequired = vm.getIsRequired(row);
		const errorKey = getValidationErrorKey("salesperson", job.id);
		const hasError = !!validationErrors?.[errorKey];

		// View Mode: render salesperson name for performance
		if (!isInEditMode) {
			const displayName = selectedSalesperson?.name ?? "";
			return (
				<Box
					title={hasError ? validationErrors[errorKey] : displayName}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayName || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		return (
			<Autocomplete
				size="small"
				freeSolo
				style={{ width: "100%", padding: 0 }}
				value={selectedSalesperson}
				options={allSalespersons}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionLabel={(option) => {
					if (typeof option === "string") {
						return option.startsWith("New Salesperson") ? option : `New Salesperson: ${option}`;
					}
					return option?.name ?? "";
				}}
				filterOptions={(options, params) => {
					const filtered = filter(options, params);

					if (
						params.inputValue !== "" &&
						!allSalespersons.some((salesperson) => salesperson.name.toLowerCase() === params.inputValue.toLowerCase())
					) {
						filtered.push(newSalespersonContact(CREATE_NEW_SALESPERSON, `New Salesperson: ${params.inputValue}`));
					}

					return filtered;
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						style={{ padding: 0 }}
						error={hasError}
						helperText={validationErrors?.[errorKey]}
						required={isRequired}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(job.id);
							}
						}}
					/>
				)}
				onChange={(_event, value, reason) => {
					vm.handleChange(job.id, value, reason, row);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for better memoization
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.salespersonIds?.join(",") === nextJob.salespersonIds?.join(",") &&
			prevJob.newSalesperson?.name === nextJob.newSalesperson?.name
		);
	},
);

interface SalespersonColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: SalespersonColumnViewModel;
}

/**
 * Memoized AggregatedCell component for Salesperson column
 * Renders editable Autocomplete when group row is in edit mode
 */
const SalespersonColumnAggregatedCell = React.memo(
	function SalespersonColumnAggregatedCell({ row, vm }: SalespersonColumnAggregatedCellProps) {
		// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
		const isInEditMode = useIsRowInEditMode(row.id);
		const { exitRowEdit } = useEditModeContext();

		// Get value from first job in group
		const job = row.original;
		const allSalespersons = vm.getAllSalespersons();
		const selectedSalesperson = vm.getSelectedSalesperson(job);

		// View Mode: render salesperson name
		if (!isInEditMode) {
			const displayName = selectedSalesperson?.name ?? "";
			return (
				<Box
					title={displayName || undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayName || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		// Edit Mode: render Autocomplete that updates all rows in group
		return (
			<Autocomplete
				size="small"
				freeSolo
				style={{ width: "100%", padding: 0 }}
				value={selectedSalesperson}
				options={allSalespersons}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionLabel={(option) => {
					if (typeof option === "string") {
						return option.startsWith("New Salesperson") ? option : `New Salesperson: ${option}`;
					}
					return option?.name ?? "";
				}}
				filterOptions={(options, params) => {
					const filtered = filter(options, params);

					if (
						params.inputValue !== "" &&
						!allSalespersons.some((salesperson) => salesperson.name.toLowerCase() === params.inputValue.toLowerCase())
					) {
						filtered.push(newSalespersonContact(CREATE_NEW_SALESPERSON, `New Salesperson: ${params.inputValue}`));
					}

					return filtered;
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						style={{ padding: 0 }}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(row.id);
							}
						}}
					/>
				)}
				onChange={(_event, value, reason) => {
					vm.handleGroupChange(row, value, reason);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.salespersonIds?.join(",") === nextJob.salespersonIds?.join(",") &&
			prevJob.newSalesperson?.name === nextJob.newSalesperson?.name
		);
	},
);

/**
 * Build the Salesperson column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function column_Salesperson(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	const vm = useSalespersonColumnViewModel(context);

	return {
		id: "salesperson",
		header: columnDisplayNames.salesperson,
		accessorKey: "salespersonIds",
		enableEditing: false,
		size: 300,
		Cell: ({ row }: { row: MRT_Row<Job> }) => <SalespersonColumnCell row={row} vm={vm} />,
		AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => <SalespersonColumnAggregatedCell row={row} vm={vm} />,
	};
}
